import type { ReactNode } from 'react';
import { Button, Dropdown } from 'antd';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import ToolOutlined from '@ant-design/icons/ToolOutlined';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from '@/features/shell/useViewport';
import styles from '@/styles/ChatWindow.module.scss';

interface ChatToolbarProps {
  /** Recent Q&As, New Q&A and the title (from the chat page). */
  leading?: ReactNode;
  hasInquiry: boolean;
  /** False while the Q&A has no messages: CSV shows, disabled (kit). */
  canExport: boolean;
  onExportCsv: () => void;
  onEditPrompt: () => void;
}

/**
 * The Q&A toolbar (spec §3): recent Q&As, New Q&A, title, prompt editing and
 * CSV. Below 900 px prompt editing and CSV move into More (spec §2).
 */
const ChatToolbar = ({
  leading,
  hasInquiry,
  canExport,
  onExportCsv,
  onEditPrompt,
}: ChatToolbarProps) => {
  const { t } = useTranslation();
  const { medium } = useBreakpoints();

  const actions = medium ? (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items: [
          {
            key: 'prompt',
            icon: <ToolOutlined aria-hidden />,
            label: t('home.chat.prompt'),
            onClick: onEditPrompt,
          },
          ...(hasInquiry
            ? [
                {
                  key: 'csv',
                  icon: <DownloadOutlined aria-hidden />,
                  label: t('home.chat.csvExport.button'),
                  disabled: !canExport,
                  onClick: () => onExportCsv(),
                },
              ]
            : []),
        ],
      }}
    >
      <Button
        className={styles.toolbarIcon}
        type="text"
        icon={<MoreOutlined aria-hidden />}
        aria-label={t('v2.common.more')}
        title={t('v2.common.more')}
        data-testid="toolbar-more"
      />
    </Dropdown>
  ) : (
    <>
      <Button
        type="text"
        size="small"
        className={styles.prompt}
        icon={<ToolOutlined aria-hidden />}
        onClick={onEditPrompt}
      >
        {t('home.chat.prompt')}
      </Button>
      {hasInquiry && (
        <Button
          type="text"
          size="small"
          className={styles.prompt}
          icon={<DownloadOutlined aria-hidden />}
          disabled={!canExport}
          onClick={() => onExportCsv()}
        >
          {t('home.chat.csvExport.button')}
        </Button>
      )}
    </>
  );

  return (
    <div className={styles.header}>
      <div className={styles.toolbarStart}>{leading}</div>
      <div className={styles.toolbarEnd}>{actions}</div>
    </div>
  );
};

export default ChatToolbar;
